/**
 * AuthGuard Component
 * 
 * A global authentication guard that ensures the application correctly redirects
 * to the /login route whenever the user's authentication state is invalid.
 * 
 * Features:
 * - Checks for valid auth token/session on app load, refresh, or window reopen
 * - Redirects to /login if session is expired (24-hour expiry) or token validation fails
 * - Prevents infinite loops by allowing authenticated users to continue normally
 * - Handles intermediate auth routes (/auth, /callback, /verify)
 * - Synchronous redirect before UI rendering to avoid flicker
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { LOGOUT } from '../../Container/Login/constants';
import { loadUser } from '../../Container/Login/actions';
import { 
  hasValidToken, 
  isTokenExpired, 
  clearAuthData,
  updateLastActivity,
  isSessionTimedOut
} from '../../helper/tokenUtils';
import setAuthToken from '../../helper/setAuthToken';

// Routes that don't require authentication
const PUBLIC_ROUTES = [
  '/admin/login',
  '/admin/register',
  '/admin/forgot-password',
  '/admin/reset-password',
  '/error',
  '/unauthorized',
  '/success'
];

// Intermediate auth routes that should redirect to login if unauthenticated
const INTERMEDIATE_AUTH_ROUTES = [
  '/auth',
  '/callback',
  '/verify'
];

// Check if path matches any of the given routes
const matchesRoute = (path, routes) => {
  const normalizedPath = path.toLowerCase();
  return routes.some(route => 
    normalizedPath === route.toLowerCase() || 
    normalizedPath.startsWith(route.toLowerCase() + '/')
  );
};

const AuthGuard = ({ 
  children, 
  auth, 
  dispatch 
}) => {
  const history = useHistory();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const checkInProgress = useRef(false);
  const redirectInProgress = useRef(false);

  // Memoized redirect function to prevent loops
  const redirectToLogin = useCallback((reason = '') => {
    if (redirectInProgress.current) return;
    
    const currentPath = location.pathname;
    
    // Don't redirect if already on a public route
    if (matchesRoute(currentPath, PUBLIC_ROUTES)) {
      return;
    }
    
    redirectInProgress.current = true;
    
    console.log(`Auth Guard: Redirecting to login - ${reason}`);
    
    // Clear auth data
    clearAuthData();
    dispatch({ type: LOGOUT });
    
    // Redirect to login with the intended destination
    history.replace({
      pathname: '/admin/login',
      state: { from: location, reason }
    });
    
    // Reset redirect flag after a short delay
    setTimeout(() => {
      redirectInProgress.current = false;
    }, 100);
  }, [location, history, dispatch]);

  // Main authentication check function
  const performAuthCheck = useCallback(() => {
    if (checkInProgress.current) return;
    checkInProgress.current = true;

    const currentPath = location.pathname;
    const token = localStorage.getItem('token');

    console.log('Auth Guard: Checking authentication for path:', currentPath);

    // Allow public routes without auth check
    if (matchesRoute(currentPath, PUBLIC_ROUTES)) {
      console.log('Auth Guard: Public route, skipping auth check');
      setIsChecking(false);
      setIsInitialized(true);
      checkInProgress.current = false;
      return;
    }

    // Handle intermediate auth routes - redirect to login if no valid token
    if (matchesRoute(currentPath, INTERMEDIATE_AUTH_ROUTES)) {
      if (!hasValidToken()) {
        console.log('Auth Guard: Intermediate route without valid token');
        redirectToLogin(''); // Silent redirect
        checkInProgress.current = false;
        return;
      }
    }

    // No token exists - redirect silently (first time visitor or cleared storage)
    if (!token) {
      console.log('Auth Guard: No token found');
      redirectToLogin(''); // No message for first-time visitors
      checkInProgress.current = false;
      return;
    }

    // Token is expired - show message
    if (isTokenExpired(token)) {
      console.log('Auth Guard: Token expired');
      redirectToLogin('Your session has expired');
      checkInProgress.current = false;
      return;
    }

    // Session timeout check (inactivity)
    if (isSessionTimedOut()) {
      console.log('Auth Guard: Session timed out due to inactivity');
      redirectToLogin('Session timed out');
      checkInProgress.current = false;
      return;
    }

    // Token is valid - set it in axios headers and update activity
    setAuthToken(token);
    updateLastActivity();

    // If auth state is not yet loaded, trigger load
    if (!auth.isAuthenticated && auth.loading) {
      dispatch(loadUser());
    }

    setIsChecking(false);
    setIsInitialized(true);
    checkInProgress.current = false;
  }, [location.pathname, auth.isAuthenticated, auth.loading, dispatch, redirectToLogin]);

  // Initial authentication check on mount
  useEffect(() => {
    performAuthCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-check on route changes
  useEffect(() => {
    if (isInitialized) {
      performAuthCheck();
    }
  }, [location.pathname, isInitialized, performAuthCheck]);

  // Handle auth state changes from Redux
  useEffect(() => {
    if (!isInitialized) return;

    const currentPath = location.pathname;
    
    // Skip if on public routes
    if (matchesRoute(currentPath, PUBLIC_ROUTES)) {
      return;
    }

    // If auth finished loading and user is not authenticated, check token before redirecting
    if (!auth.loading && auth.isAuthenticated === false) {
      // Double-check: only redirect if there's truly no valid token
      // This prevents race conditions where isAuthenticated is false but token is still valid
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('Auth Guard: No token found, redirecting to login');
        redirectToLogin('');
      } else if (isTokenExpired(token)) {
        console.log('Auth Guard: Token expired, redirecting to login');
        redirectToLogin('Session has expired');
      } else {
        // Token exists and is valid but Redux says not authenticated
        // This is a sync issue - trigger a reload of user data instead of logout
        console.log('Auth Guard: Valid token exists but auth state is false - reloading user');
        setAuthToken(token);
        dispatch(loadUser());
      }
    }
  }, [auth.loading, auth.isAuthenticated, isInitialized, location.pathname, redirectToLogin, dispatch]);

  // Listen for storage events (logout from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        console.log('Auth Guard: Token removed from another tab');
        redirectToLogin('Logged out from another tab');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [redirectToLogin]);

  // Listen for visibility changes (tab becomes visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Auth Guard: Tab became visible, re-checking auth');
        performAuthCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [performAuthCheck]);

  // Listen for window focus (window regains focus)
  useEffect(() => {
    const handleFocus = () => {
      console.log('Auth Guard: Window focused, re-checking auth');
      performAuthCheck();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [performAuthCheck]);

  // Periodic check for token expiration (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && isTokenExpired(token)) {
        console.log('Auth Guard: Periodic check found expired token');
        redirectToLogin('Session expired');
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [redirectToLogin]);

  // Update activity on user interaction
  useEffect(() => {
    const handleActivity = () => {
      updateLastActivity();
    };

    // Throttled activity update
    let activityTimeout;
    const throttledActivityHandler = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        handleActivity();
        activityTimeout = null;
      }, 60000); // Update at most once per minute
    };

    window.addEventListener('click', throttledActivityHandler);
    window.addEventListener('keypress', throttledActivityHandler);
    window.addEventListener('scroll', throttledActivityHandler);

    return () => {
      window.removeEventListener('click', throttledActivityHandler);
      window.removeEventListener('keypress', throttledActivityHandler);
      window.removeEventListener('scroll', throttledActivityHandler);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, []);

  // Show minimal loading state while checking auth
  // This prevents flicker by not rendering content until auth is verified
  if (isChecking && !matchesRoute(location.pathname, PUBLIC_ROUTES)) {
    return (
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        <div className="loading" />
      </div>
    );
  }

  return <>{children}</>;
};

const mapStateToProps = state => ({
  auth: state.auth
});

export default connect(mapStateToProps)(AuthGuard);
